"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ChevronRight, Loader2 } from "lucide-react";

import Footer from "../components/layout/footer";
import Header from "../components/layout/header";
import News from "../components/settings/news";
import Image from "next/image";

import masjid from "../assets/masjid.png";
import cafetaria from "../assets/cafetaria.png";
import mice from "../assets/mice.png";
import Link from "next/link";
import asrama from "../assets/asrama.png";
import teleconference from "../assets/teleconference.jpg";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const COLORS = {
  primary: "#003B5C",
  teal: "#00778B",
  gray: "#C7C9C7",
  gold: "#E3A130",
};

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

// ==========================
// UTILS
// ==========================
const waLink = (raw) => {
  if (!raw) return null;
  let num = raw.replace(/[^0-9]/g, "");
  if (num.startsWith("0")) num = "62" + num.slice(1);
  return `https://wa.me/${num}`;
};

const formatPriceDot = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return "0.00";
  const sign = num < 0 ? "-" : "";
  const n = Math.abs(num);
  const [int, dec] = n.toFixed(2).split(".");
  const intWithDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${intWithDots}.${dec}`;
};

const StarRating = ({ value = 0 }) => {
  const full = Math.floor(value);
  const half = value % 1 >= 0.5;
  const stars = [];
  for (let i = 0; i < full; i++)
    stars.push(
      <span key={`f-${i}`} className="w-4 h-4 text-yellow-500">
        ★
      </span>
    );
  if (half)
    stars.push(
      <span key="half" className="w-4 h-4 text-yellow-500 opacity-70">
        ★
      </span>
    );
  while (stars.length < 5)
    stars.push(
      <span key={`e-${stars.length}`} className="w-4 h-4 text-gray-300">
        ★
      </span>
    );
  return <div className="flex">{stars}</div>;
};

/**
 * ProductCard
 * - menerima props: product, href (optional), onAddToCart
 * - addToCart pakai localStorage + limit by stock
 */
function ProductCard({ product, href, onAddToCart }) {
  // pick the “front” image, or first one
  const getFrontImage = () => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      const front = product.images.find((img) => img.type === "front");
      return front?.url || product.images[0].url;
    }
    return "/product-placeholder.jpg";
  };

  // addToCart + limit <= stock
  const handleAddToCart = () => {
    try {
      const stock = Number(product.stock) || 0;

      if (stock <= 0) {
        alert("Maaf, stok habis.");
        return;
      }

      const raw = localStorage.getItem("cart") || "[]";
      const cart = JSON.parse(raw);
      if (!Array.isArray(cart)) throw new Error("Cart is not array");

      const existing = cart.find((item) => item.id === product.id);

      if (existing) {
        const currentQty = Number(existing.quantity || 0);

        if (currentQty >= stock) {
          alert(`Maksimal pembelian untuk produk ini adalah ${stock} pcs.`);
          return;
        }

        existing.quantity = currentQty + 1;
      } else {
        cart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          image: getFrontImage(),
          quantity: 1,
          store: {
            id: product.vendor?.id ?? "unknown",
            name: product.vendor?.storeName ?? "Unknown Store",
            phone: product.vendor?.phone ?? "",
          },
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      // broadcast ke listener lain (header, dsb.)
      window.dispatchEvent(new Event("cartUpdated"));
      // kalau parent mau ikut tahu
      if (onAddToCart) onAddToCart(product);
    } catch (e) {
      console.error("Cart error:", e);
    }
  };

  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? {
        href,
        className: "relative cursor-pointer block",
      }
    : {
        className: "relative cursor-pointer",
      };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition border border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900"
      style={{ borderTop: `4px solid ${COLORS.primary}` }}
    >
      <Wrapper {...wrapperProps}>
        <img
          src={getFrontImage()}
          alt={product.name}
          className="w-full h-64 object-cover"
        />
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold bg-red-500 px-2 py-1 rounded">
              Out of Stock
            </span>
          </div>
        )}
      </Wrapper>
      <div className="p-4 space-y-2">
        <h3 className="text-sm sm:text-base font-medium line-clamp-1 hover:underline">
          {href ? (
            <a href={href}>{product.name}</a>
          ) : (
            <span>{product.name}</span>
          )}
        </h3>
        <StarRating value={product.rating || 4.5} />
        <div
          className="text-xs text-zinc-500 dark:text-zinc-400 prose max-h-[3rem] overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: product.description || "<p>No description available</p>",
          }}
        />
        <div className="flex items-center justify-between pt-2 gap-2">
          <div>
            <span className={`text-lg font-semibold ${tw.tealText}`}>
              Rp{formatPriceDot(product.price)}
            </span>
            {product.stock > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                {product.stock} in stock
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
            className="px-3 py-1.5 text-xs rounded-full border border-[#00778B] text-[#00778B] hover:bg-[#00778B] hover:text-white transition"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ==========================
// MAIN PAGE
// ==========================
export default function HomePage() {
  const [mounted, setMounted] = useState(false);
 
  // ---------- Product slider ----------
  const PRODUCT_PER_SLIDE = 6;
  const [prodPage, setProdPage] = useState(1);

  // ---------- CART ----------
  const [cart, setCart] = useState([]);

  // hydrate cart from localStorage
  useEffect(() => {
    setMounted(true);
    try {
      const raw = localStorage.getItem("cart") || "[]";
      const initialCart = JSON.parse(raw);
      if (Array.isArray(initialCart)) setCart(initialCart);
    } catch {
      setCart([]);
    }

    const handleCartUpdated = () => {
      try {
        const raw = localStorage.getItem("cart") || "[]";
        const newCart = JSON.parse(raw);
        if (Array.isArray(newCart)) setCart(newCart);
      } catch {
        setCart([]);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdated);
    return () => window.removeEventListener("cartUpdated", handleCartUpdated);
  }, []);

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart]
  );

  return (
    <div>
      {/* KIRIM CART COUNT KE HEADER */}
      <Header cartCount={cartCount} />

      {/* ================= HERO: ASRAMA STATIC ================= */}
      <section className="relative" id="hero">
        <div className="relative h-[60vh] sm:h-[70vh] w-full overflow-hidden">
          {/* Background foto asrama */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${asrama.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {/* Overlay + text center */}
          <div
            className="relative h-full w-full flex items-center justify-center"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,59,92,0.85) 0%, rgba(0,59,92,0.6) 50%, rgba(0,59,92,0.4) 100%)",
            }}
          >
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow text-center"
              >
                “Enterprise for a Global Islamic University”
              </h1>

              {/* <p className="mt-4 text-sm sm:text-base md:text-lg text-zinc-100">
                
              </p> */}
            </div>
          </div>
        </div>
      </section>

      {/* ================= SECTION: MICE & ACCOMMODATION ================= */}
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-0 grid md:grid-cols-2 gap-8 items-stretch">
          {/* Left image */}
          <div className="rounded-2xl overflow-hidden shadow-sm">
            <Image
              src={teleconference}
              alt="UIII Convention Hall"
              className="w-full h-full object-cover"
              priority
              sizes="(min-width: 768px) 50vw, 100vw"
            />
          </div>

          {/* Right content */}
          <div className="flex flex-col justify-center">
            <p className={`text-sm sm:text-base mb-6 ${tw.primaryText}`}>
              UIII offers rental of meeting rooms, conference spaces, event
              venues, and accommodations with a unique ambience.
            </p>

            <div className="space-y-3">
              {/* MICE row */}
              <div className="flex items-center justify-between bg-zinc-50 rounded-xl px-4 py-3 border border-zinc-100">
                <span className="font-semibold text-sm sm:text-base">
                  MICE
                </span>
                <Link href="/mice">
                  <button className="px-4 py-1.5 text-xs sm:text-sm rounded-full bg-[#003B5C] text-white hover:bg-[#00263c]">
                    View More
                  </button>
                </Link>
              </div>

              <div className="flex items-center justify-between bg-zinc-50 rounded-xl px-4 py-3 border border-zinc-100">
                <span className="font-semibold text-sm sm:text-base">
                  Accommodation
                </span>
                <Link href="/accomodation">
                  <button className="px-4 py-1.5 text-xs sm:text-sm rounded-full bg-[#003B5C] text-white hover:bg-[#00263c]">
                    View More
                  </button>
                </Link>
              </div>

              {/* Accommodation row */}
              
            </div>
          </div>
        </div>
      </section>

      {/* ================= SECTION: MERCHANDISE ================= */}
      <section className="bg-zinc-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-0 grid md:grid-cols-2 gap-8 items-stretch">
          {/* Left text */}
          <div className="bg-white rounded-2xl shadow-sm p-8 flex flex-col justify-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#003B5C] mb-4">
              Merchandise
            </h2>
            <p className="text-sm sm:text-base text-zinc-700 leading-relaxed mb-6">
              We pride ourselves on delivering an authentic UIII identity. Each
              design is crafted for the alumni, students, faculty, fans, and
              visitors of UIII.
            </p>
            <Link href="/merchandise">
              <button className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold bg-[#003B5C] text-white hover:bg-[#00263c] w-max">
                View More
              </button>
            </Link>
          </div>

          {/* Right collage image */}
          <div className="rounded-2xl overflow-hidden shadow-sm">
            <Image
              src={mice}
              alt="UIII Official Merchandise"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ================= SECTION: CAFETERIA MENU ================= */}
      <section id="cafeteria" className="bg-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-0 grid md:grid-cols-2 gap-8 items-stretch">
          {/* Left cafeteria image */}
          <div className="rounded-2xl overflow-hidden shadow-sm order-2 md:order-1">
            <Image
              src={cafetaria}
              alt="UIII Cafeteria"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right text */}
          <div className="flex flex-col justify-center order-1 md:order-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#003B5C] mb-4">
              Today&apos;s Cafeteria Menu
            </h2>
            <p className="text-sm sm:text-base text-zinc-700 mb-6">
              Have you eaten? Let&apos;s check UIII cafeteria menu today!
            </p>
            <Link href="/f&b">
              <button className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold bg-[#003B5C] text-white hover:bg-[#00263c] w-max">
                Choose the menu
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ================= NEWS ================= */}
      <News />

      <Footer />
    </div>
  );
}
